import { useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import DramaCard from '@/components/feature/DramaCard';
import FilterBar from './components/FilterBar';
import { dramas } from '@/mocks/dramas';

export default function DramaList() {
  const [params, setParams] = useSearchParams();
  const activeCat = params.get('cat') || '全部';
  const search = params.get('q') || '';

  useEffect(() => {
    document.title = '短剧档案库｜爆料短剧真实事件改编对照检索 - 剧真档案';
  }, []);

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value && value !== '全部') {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    setParams(next, { replace: true });
  };

  const [sort, setSortState] = [params.get('sort') || 'hot', (v: string) => setParam('sort', v)];

  const filtered = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    let list = dramas.filter((d) => {
      const catOk = activeCat === '全部' || d.category === activeCat;
      if (!catOk) return false;
      if (!keyword) return true;
      const haystack = [d.title, d.subtitle, d.category, d.confirmLevel, ...d.tags]
        .join(' ')
        .toLowerCase();
      return haystack.includes(keyword);
    });

    list = [...list].sort((a, b) => {
      if (sort === 'score') return b.adaptationScore - a.adaptationScore;
      if (sort === 'recent') return a.publishDate < b.publishDate ? 1 : -1;
      return b.hot - a.hot;
    });
    return list;
  }, [activeCat, search, sort]);

  const collectionJsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'CollectionPage',
        name: '短剧档案库｜爆料短剧真实事件改编对照检索',
        url: 'https://aceasia.cn/dramas',
        inLanguage: 'zh-CN',
        description:
          '汇集已归档的爆料短剧，可按题材筛选，查看真实事件改编确认、原型人物与改编程度分析。',
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://aceasia.cn/' },
          {
            '@type': 'ListItem',
            position: 2,
            name: '短剧档案库',
            item: 'https://aceasia.cn/dramas',
          },
        ],
      },
    ],
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Navbar />
      <main className="flex-1 pt-16 md:pt-[72px]">
        <section className="w-full bg-background-100 border-b border-background-200 archive-grid">
          <div className="w-full px-4 md:px-8 py-8 md:py-12">
            <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '短剧档案库' }]} />
            <div className="mt-5 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
              <div className="max-w-3xl">
                <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight">
                  短剧档案库
                </h1>
                <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                  这里收录已核对过的爆料短剧。每一条都标注了改编确认状态、原型人物对照与综合还原度评分，可先按题材筛选，再进去逐条比对。
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {['已确认改编', '高度疑似', '部分取材', '声明存疑'].map((level) => (
                  <span
                    key={level}
                    className="px-3 py-1.5 rounded-md bg-background-50 border border-background-300 text-xs text-foreground-700 whitespace-nowrap"
                  >
                    {level}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        <FilterBar
          search={search}
          onSearch={(v) => setParam('q', v)}
          activeCat={activeCat}
          onCat={(v) => setParam('cat', v)}
          sort={sort}
          onSort={setSortState}
          total={filtered.length}
        />

        <section className="w-full px-4 md:px-8 py-8 md:py-12">
          {filtered.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {filtered.map((drama) => (
                <DramaCard key={drama.id} drama={drama} />
              ))}
            </div>
          ) : (
            <div className="py-20 flex flex-col items-center text-center">
              <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-file-search-line text-2xl"></i>
              </span>
              <h2 className="mt-5 font-heading text-lg font-bold text-foreground-950">
                没有匹配的档案
              </h2>
              <p className="mt-2 text-sm text-foreground-600 max-w-md">
                换个关键词，或者清空筛选条件看看全部已归档的短剧。
              </p>
              <button
                type="button"
                onClick={() => setParams(new URLSearchParams(), { replace: true })}
                className="mt-6 px-5 py-3 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                清空筛选条件
              </button>
            </div>
          )}

          <div className="mt-12 p-5 md:p-6 rounded-lg bg-secondary-50 border border-secondary-200 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
            <span className="w-11 h-11 shrink-0 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
              <i className="ri-add-circle-line text-xl"></i>
            </span>
            <div className="flex-1">
              <h2 className="font-heading text-base font-bold text-foreground-950">
                没找到你想看的那部剧？
              </h2>
              <p className="mt-1.5 text-sm text-foreground-600">
                把剧名和你在意的疑点告诉我们，我们会排进核对队列，出结果后同步到档案库。
              </p>
            </div>
            <Link
              to="/about#contact"
              className="self-start sm:self-auto px-5 py-3 rounded-md border border-secondary-300 text-sm text-secondary-900 hover:bg-background-50 transition-colors whitespace-nowrap"
            >
              提交核对线索
            </Link>
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(collectionJsonLd) }}
      />
    </div>
  );
}